import {
  Copy,
  CopyPlus,
  EyeOff,
  Filter,
  FilterX,
  Group,
  LineChart,
  List,
  ListPlus,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCellFilter } from "@/hooks/useUrlFilters";

/** What was right-clicked. Null closes the menu. */
export type CellTarget = {
  x: number;
  y: number;
  /** Column header, for the menu's own label. */
  columnLabel: string;
  /** The cell's text. Absent on a header — a header has no value to act on. */
  value?: string;
  /**
   * The filter key in `clicks_dimensions()`, or absent when the column is a
   * measurement. A number has no "show me only this value" that anyone wants.
   */
  filterKey?: string;
  /** The Group By option for this column, when it has one worth offering. */
  groupByValue?: string;
  /** The column to remove from the picker. Headers only. */
  hideColumnId?: string;
  /**
   * Every value in this column across the current (filtered) view, for
   * "copy column as list" — paste into a filter or a spreadsheet. Absent on
   * columns whose values the grid can't hand over (e.g. a metric cell).
   */
  columnValues?: string[];
};

interface CellContextMenuProps {
  target: CellTarget | null;
  onClose: () => void;
  onHideColumn?: (columnId: string) => void;
  /** Scope the trend chart to this cell's value (drill-down). */
  onFocusChart?: (focus: { key: string; value: string; label: string }) => void;
}

/**
 * The right-click menu, in the spirit of a spreadsheet. Works on a cell and on
 * a column header; what it offers depends on what was clicked.
 *
 * Filters are written to the URL through useCellFilter - the same writer as the
 * filter bar and the buttons in the cell - so they cannot drift apart: one
 * filter model, one place it lives, the back button undoes a filter set from
 * here, and the address bar is a shareable link to what the table shows.
 */
export function CellContextMenu({
  target,
  onClose,
  onHideColumn,
  onFocusChart,
}: CellContextMenuProps) {
  const [, setSearchParams] = useSearchParams();
  const cellFilter = useCellFilter();

  if (!target) return null;

  const applyFilter = (mode: "include" | "exclude") => {
    if (!target.filterKey || target.value === undefined) return;
    cellFilter(target.filterKey, target.value, mode, target.columnLabel);
    onClose();
  };

  // Unlike applyFilter, this keeps the values already there and appends - the
  // way you build an "is one of A, B, C" filter by clicking cells one by one.
  const addToFilter = () => {
    if (!target.filterKey || target.value === undefined) return;
    cellFilter(target.filterKey, target.value, "add", target.columnLabel);
    onClose();
  };

  const groupBy = () => {
    if (!target.groupByValue) return;
    // groupDims is what the page reads first (Clicks.tsx); setting only the
    // legacy groupBy did nothing once the grouping had been changed.
    setSearchParams((prev) => {
      prev.set("groupDims", target.groupByValue as string);
      prev.delete("groupBy");
      prev.set("page", "1");
      return prev;
    });
    onClose();
  };

  const hide = () => {
    if (!target.hideColumnId) return;
    onHideColumn?.(target.hideColumnId);
    toast.success(`Hid ${target.columnLabel}`, {
      description: "Bring it back from the column list",
    });
    onClose();
  };

  const copy = async () => {
    if (target.value === undefined) return;
    try {
      await navigator.clipboard.writeText(target.value);
      toast.success("Copied", { description: target.value });
    } catch {
      toast.error("Could not copy — the clipboard is not available here");
    }
    onClose();
  };

  const copyWithHeader = async () => {
    if (target.value === undefined) return;
    // Header then value on two lines: pastes into a spreadsheet as a labelled
    // one-column pair, which is what "copy with header" means elsewhere.
    try {
      await navigator.clipboard.writeText(
        `${target.columnLabel}\n${target.value}`,
      );
      toast.success("Copied with header", { description: target.columnLabel });
    } catch {
      toast.error("Could not copy — the clipboard is not available here");
    }
    onClose();
  };

  const copyColumn = async () => {
    if (!target.columnValues?.length) return;
    try {
      await navigator.clipboard.writeText(target.columnValues.join("\n"));
      toast.success(`Copied ${target.columnValues.length} values`, {
        description: target.columnLabel,
      });
    } catch {
      toast.error("Could not copy — the clipboard is not available here");
    }
    onClose();
  };

  const hasColumnValues = Boolean(target.columnValues?.length);
  const canFilter = Boolean(target.filterKey) && target.value !== undefined;
  const canCopy = target.value !== undefined;

  return (
    <DropdownMenu
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {/* A zero-size anchor parked where the pointer was: the menu belongs to a
          point on the screen, not to any one element, and giving each cell its
          own Radix root would mount one menu per cell on every render. */}
      <DropdownMenuTrigger
        aria-hidden
        tabIndex={-1}
        className="pointer-events-none fixed size-0 p-0"
        style={{ left: target.x, top: target.y }}
      />
      <DropdownMenuContent align="start" className="w-60">
        <DropdownMenuLabel className="truncate text-xs font-normal text-muted-foreground">
          {canCopy ? `${target.columnLabel}: ${target.value}` : target.columnLabel}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {canFilter ? (
          <>
            <DropdownMenuItem onSelect={() => applyFilter("include")}>
              <Filter className="size-4" />
              Filter by this value
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={addToFilter}>
              <ListPlus className="size-4" />
              Add to filter
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => applyFilter("exclude")}>
              <FilterX className="size-4" />
              Exclude this value
            </DropdownMenuItem>
            {onFocusChart ? (
              <DropdownMenuItem
                onSelect={() => {
                  if (target.filterKey && target.value !== undefined) {
                    onFocusChart({
                      key: target.filterKey,
                      value: target.value,
                      label: `${target.columnLabel}: ${target.value}`,
                    });
                  }
                  onClose();
                }}
              >
                <LineChart className="size-4" />
                Show in chart
              </DropdownMenuItem>
            ) : null}
          </>
        ) : null}

        {target.groupByValue ? (
          <DropdownMenuItem onSelect={groupBy}>
            <Group className="size-4" />
            Group by {target.columnLabel}
          </DropdownMenuItem>
        ) : null}

        {target.hideColumnId ? (
          <DropdownMenuItem onSelect={hide}>
            <EyeOff className="size-4" />
            Hide this column
          </DropdownMenuItem>
        ) : null}

        {(canCopy || hasColumnValues) &&
        (canFilter || target.groupByValue || target.hideColumnId) ? (
          <DropdownMenuSeparator />
        ) : null}

        {canCopy ? (
          <>
            <DropdownMenuItem onSelect={copy}>
              <Copy className="size-4" />
              Copy cell
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={copyWithHeader}>
              <CopyPlus className="size-4" />
              Copy with header
            </DropdownMenuItem>
          </>
        ) : null}

        {hasColumnValues ? (
          <DropdownMenuItem onSelect={copyColumn}>
            <List className="size-4" />
            Copy column as list
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
